import { differenceInDays } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import Highcharts, { Options as HighchartsOptions, SeriesColumnOptions, SeriesOptions, XAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsBoost from 'highcharts/modules/boost';
import debounce from 'lodash.debounce';
import { equals } from 'ramda';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';

import { useAuth } from '../../app/AuthorizationProvider';
import { getZonedDateTimezoneOffset } from '../../utils/date';
import { convertFieldsEndOfDateRange } from '../../utils/dateRange';
import { staticBarChartOptions, TRANSITION_TIME_DELAY } from './config';
import { BarChartProps } from './types';
import { barChartTooltipFormatter } from './utils';

if (process.env.NODE_ENV !== 'test') {
  /**
   * Disable in test environment
   * see https://github.com/highcharts/highcharts/issues/13599#issuecomment-639373736
   */
  HighchartsBoost(Highcharts);
}

const generateChartConfig: (props: BarChartProps, containerWidth: number, locale: string) => HighchartsOptions = (
  {
    data,
    handleSelectDate,
    isContainerExpanded,
    isContainerOpen,
    maxDate,
    minDate,
    pickedDate,
    t,
    timezone,
    yAxisTicks,
    localeObj,
  },
  containerWidth,
  locale
) => {
  // Following "magic" numbers were chosen in order to both column's width and padding could fit the most the available space
  const countOfData = data?.adherence.length ?? 1;
  const widthPerDataInterval = isContainerExpanded ? containerWidth / 8.4 : containerWidth / 4.8;
  const constrainedWidthPerInterval = Math.max(widthPerDataInterval, 95);
  const chartWidth = Math.max(constrainedWidthPerInterval * countOfData, containerWidth);

  const dateFormat = t('date.formats.default');

  return {
    ...staticBarChartOptions,
    chart: {
      ...staticBarChartOptions.chart,
      width: chartWidth,
    },
    plotOptions: {
      ...staticBarChartOptions.plotOptions,
      column: {
        ...staticBarChartOptions?.plotOptions?.column,
        groupPadding: isContainerExpanded ? 0.3 : 0.35,
        pointPadding: isContainerExpanded ? 0.05 : 0.08,
        events: {
          click(event: Highcharts.SeriesClickEventObject) {
            handleSelectDate(event.point.x);
          },
        },
      },
    },
    series: [
      {
        ...(staticBarChartOptions.series as SeriesOptions[])[0],
        data: [...(data?.adherence ?? [])],
        name: t('scheduleAdherence.inAdherence'),
        type: 'column',
      },
      {
        ...(staticBarChartOptions.series as SeriesOptions[])[1],
        data: [...(data?.conformance ?? [])],
        name: t('scheduleAdherence.conformance'),
        type: 'column',
      },
    ] as SeriesColumnOptions[],
    time: {
      getTimezoneOffset: (timestamp: number) =>
        getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
      useUTC: true,
    },
    tooltip: {
      ...staticBarChartOptions.tooltip,
      formatter: barChartTooltipFormatter(t, locale, data),
    },
    xAxis: {
      ...staticBarChartOptions.xAxis,
      max: convertFieldsEndOfDateRange(maxDate).getTime(),
      min: minDate,
      labels: {
        ...(staticBarChartOptions.xAxis as XAxisOptions).labels,
        formatter() {
          return `<span class="adherence-history__bar-chart__${
            isContainerOpen && pickedDate === this.value ? 'picked-date-label' : 'date-label'
          }">${format(utcToZonedTime(this.value, timezone), dateFormat, {
            locale: localeObj,
            timeZone: timezone,
          })}</span>`;
        },
      },
    },
    yAxis: {
      ...staticBarChartOptions.yAxis,
      tickAmount: yAxisTicks.length,
      tickPositions: yAxisTicks,
    },
  };
};

export const BarChartComponent: React.FC<BarChartProps> = (props) => {
  const { data, isContainerOpen, minDate, pickedDate, timezone } = props;

  const { locale } = useAuth();

  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollbarContainerRef, setScrollbarContainerRef] = useState<HTMLElement | null>(null);

  const chartRef = useRef<HighchartsReact>(null);
  const scrollbarRef = useRef<PerfectScrollbar>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateScrollPosition = useCallback(
    debounce(() => {
      scrollbarRef.current?.forceUpdate();
      if (scrollbarContainerRef) {
        scrollbarContainerRef.setAttribute('style', 'scroll-behavior: smooth;');

        if (isContainerOpen && pickedDate) {
          const widthPerDay = scrollbarContainerRef.scrollWidth / (data?.adherence.length ?? 1);
          const currentDayOffset = differenceInDays(pickedDate, minDate);
          scrollbarContainerRef.scrollLeft = widthPerDay * currentDayOffset;
        }

        scrollbarContainerRef.removeAttribute('style');
      }
    }, TRANSITION_TIME_DELAY),
    [scrollbarRef, scrollbarContainerRef, pickedDate, isContainerOpen, data, minDate]
  );

  useEffect(() => {
    if (scrollbarContainerRef) {
      scrollbarContainerRef.setAttribute('style', 'scroll-behavior: smooth;');

      if (isContainerOpen && pickedDate) {
        const widthPerDay = scrollbarContainerRef.scrollWidth / (data?.adherence.length ?? 1);
        const currentDayOffset = differenceInDays(pickedDate, minDate);
        scrollbarContainerRef.scrollLeft = widthPerDay * currentDayOffset;
      }

      scrollbarContainerRef.removeAttribute('style');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isContainerOpen, minDate, pickedDate]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      scrollbarRef.current?.forceUpdate();
    }, TRANSITION_TIME_DELAY);
    return () => {
      clearTimeout(timeOut);
    };
  }, [data]);

  const onResizeHandler = (width?: number) => {
    if (width === undefined) {
      return;
    }

    setContainerWidth(width);
    scrollbarRef.current?.forceUpdate();
    chartRef?.current?.chart.reflow();

    if (!containerWidth || !scrollbarContainerRef?.scrollWidth) {
      return;
    }
    if (scrollbarContainerRef?.scrollWidth !== undefined) {
      scrollbarContainerRef.scrollLeft = 0;
    }

    updateScrollPosition();
  };

  const onChartLoadCallback = () => {
    // Update scrollbar as the initial animation should broke calculation of size of the container
    setTimeout(() => scrollbarRef.current?.forceUpdate(), 800);
  };

  return (
    <ReactResizeDetector handleWidth onResize={onResizeHandler} refreshMode="debounce" refreshRate={5}>
      <PerfectScrollbar
        containerRef={setScrollbarContainerRef}
        ref={scrollbarRef}
        options={{ scrollingThreshold: 100, swipeEasing: true, scrollXMarginOffset: 10 }}
        className="w-100"
      >
        <div className="mb-2" data-test-id="adherence-history-detail:bar-chart">
          <HighchartsReact
            highcharts={Highcharts}
            key={timezone}
            options={generateChartConfig(props, containerWidth, locale)}
            ref={chartRef}
            callback={onChartLoadCallback}
          />
        </div>
      </PerfectScrollbar>
    </ReactResizeDetector>
  );
};

export const BarChart = memo(BarChartComponent, (
  // Purify props from provided handlers
  { handleSelectDate: _prevSelectedDate, t: _prevT, ...prevProps },
  { handleSelectDate: _nextSelectedDate, t: _nextT, ...nextProps }
) => equals(prevProps, nextProps));
